/* eslint-disable @typescript-eslint/no-namespace */
import { DealType, DealVersion, Series } from '../../types/deal.types';
import moment, { Moment } from 'moment';
import momentTz from 'moment-timezone';
import { onlyUnique } from '../../utils/filters/only-unique';
import { DesignationIssue } from '../../types/designation.types';

export namespace IssueDate {
  type dateTime = Moment | string | undefined;

  export interface DateTime {
    date: dateTime;
    time: dateTime;
    timeNoTimezone: dateTime;
  }

  export interface DealWithFormattedDate extends DealVersion {
    dealDate: Moment;
    dealDateFormatted: dateTime;
    dealTimeFormatted: dateTime;
    dealTimeNoTimezone: dateTime;
    filterOption: dateTime;
  }

  export interface DesiDealWithFormattedDate extends DesignationIssue {
    dealDate: Moment;
    dealDateFormatted: dateTime;
    dealTimeFormatted: dateTime;
    dealTimeNoTimezone: dateTime;
  }

  export enum Priority {
    date = 'Date',
    DayToDay = 'Day to Day',
    TBD = 'TBD'
  }

  export enum Formats {
    MDD = 'M/DD',
    dddMDD = 'ddd M/DD',
    hmm = 'h:mm',
    hmmaz = 'h:mm A z',
    hmma = 'h:mm A',
    hmmz = 'h:mm z'
  }

  namespace Negotiated {
    // tslint:disable-next-line:no-shadowed-variable
    export function getFormattedDealDateByPriority({
      isTBD,
      isDayToDay,
      isWeekOf,
      dealType,
      expectedPricingDate,
      saleDate
    }: DealVersion): DateTime {
      const dealDate = getDealDate({
        dealType,
        expectedPricingDate,
        saleDate
      } as DealVersion);

      if (isTBD) {
        return {
          date: Priority.TBD,
          time: undefined,
          timeNoTimezone: undefined
        };
      } else if (isDayToDay) {
        return {
          date: Priority.DayToDay,
          time: undefined,
          timeNoTimezone: undefined
        };
      } else if (isWeekOf && dealDate) {
        return {
          date: expectedPricingDate
            .startOf('week')
            .add(1, 'days')
            .format(Formats.MDD),
          time: undefined,
          timeNoTimezone: undefined
        };
      }

      return {
        date: dealDate ? dealDate.format(Formats.dddMDD) : undefined,
        time: undefined,
        timeNoTimezone: undefined
      };
    }
  }

  namespace Competitive {
    // tslint:disable-next-line:no-shadowed-variable
    export function getFormattedDealDateByPriority({
      isTBD,
      isDayToDay,
      isWeekOf,
      dealType,
      expectedPricingDate,
      saleDate
    }: DealVersion): DateTime {
      const dealDate: Moment = getDealDate({
        dealType,
        expectedPricingDate,
        saleDate
      } as DealVersion);

      const dateInEST =
        dealDate && momentTz(dealDate as any).tz('America/New_York');

      if (isTBD) {
        return {
          date: Priority.TBD,
          time: undefined,
          timeNoTimezone: undefined
        };
      } else if (isDayToDay) {
        return {
          date: Priority.DayToDay,
          time: undefined,
          timeNoTimezone: undefined
        };
      } else if (isWeekOf && dateInEST) {
        return {
          date: dateInEST.format(Formats.MDD),
          time: undefined,
          timeNoTimezone: undefined
        };
      }

      return {
        date: dateInEST ? dateInEST.format(Formats.dddMDD) : undefined,
        time: dateInEST ? dateInEST.format(Formats.hmmaz) : undefined,
        timeNoTimezone: dateInEST ? dateInEST.format(Formats.hmma) : undefined
      };
    }
  }

  export function getDealDate({
    dealType,
    saleDate,
    expectedPricingDate
  }: DealVersion): Moment | undefined {
    switch (dealType) {
      case DealType.competitive:
        return saleDate || undefined;
      case DealType.negotiated:
        return expectedPricingDate || undefined;
      default:
        return undefined;
    }
  }

  export function getFormattedDealDateByPriority(deal: DealVersion): DateTime {
    switch (deal && deal.dealType) {
      case DealType.competitive:
        return Competitive.getFormattedDealDateByPriority(deal);
      case DealType.negotiated:
        return Negotiated.getFormattedDealDateByPriority(deal);
      default:
        return {
          date: undefined,
          time: undefined,
          timeNoTimezone: undefined
        };
    }
  }

  export function toStartTimeTz(
    date: Moment,
    format: IssueDate.Formats | string
  ) {
    return momentTz(date as any)
      .tz('America/New_York')
      .format(format);
  }
}

interface DateConfig {
  format?: string;
  nullStub?: string;
  variousStub?: string;
}

export function dateFormatter(
  series: Series[],
  field: string = 'initialTradeDate',
  config?: DateConfig
): string {
  const defaultConfig: DateConfig = {
    format: 'MM/DD h:mm A z',
    nullStub: '',
    variousStub: 'Various'
  };
  const mergedConfig = {
    ...defaultConfig,
    ...config
  };
  const dates = series
    .map((oneSeries) => getDateFromSeries(oneSeries, field, mergedConfig))
    .filter(onlyUnique);
  return dates.length === 1 ? dates[0] : mergedConfig.variousStub;
}

export function getDateFromSeries(series, dateName, formatConfig) {
  const isInitialTradeDay =
    dateName === 'initialTradeDate'
      ? IssueDate.toStartTimeTz(series[dateName], formatConfig.format)
      : moment(series[dateName]).format('MM/DD/YYYY');

  return series[dateName] ? isInitialTradeDay : formatConfig.nullStub;
}
